import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import {
  faArrowLeft,
  faSearch,
  faClipboardList,
  faCheckCircle,
  faCircleXmark,
  faClock,
  faEye,
  faShoppingBag,
  faFilter,
} from "@fortawesome/free-solid-svg-icons";

import { useHerKart } from "./hooks/useHerKart"; 

const sampleOrders = [
  {
    orderNo: "FH20260710001",
    orderedAt: "2026-07-10T10:30:00Z",
    status: "Pending",
    remarks: "",
    paymentMode: "COD",

    totalItems: 4,
    grandTotal: 1139,

    customer: {
      name: "Priya Sharma",
      mobile: "9876543210",
      city: "Mumbai",
      state: "Maharashtra",
    },

    items: [
      {
        name: "Handmade Cotton Bag",
        quantity: 2,
        price: 350,
      },
      {
        name: "Organic Soap Pack",
        quantity: 1,
        price: 199,
      },
      {
        name: "Bamboo Water Bottle",
        quantity: 1,
        price: 240,
      },
    ],
  },

  {
    orderNo: "FH20260710002",
    orderedAt: "2026-07-09T16:15:00Z",
    status: "Delivered",
    remarks: "Delivered successfully",
    paymentMode: "COD",

    totalItems: 2,
    grandTotal: 398,

    customer: {
      name: "Anjali Verma",
      mobile: "9988776655",
      city: "Pune",
      state: "Maharashtra",
    },

    items: [
      {
        name: "Organic Soap Pack",
        quantity: 2,
        price: 199,
      },
    ],
  },

  {
    orderNo: "FH20260710003",
    orderedAt: "2026-07-08T12:45:00Z",
    status: "Cancelled",
    remarks: "Customer unavailable",
    paymentMode: "COD",

    totalItems: 1,
    grandTotal: 999,

    customer: {
      name: "Neha Gupta",
      mobile: "9123456780",
      city: "Nashik",
      state: "Maharashtra",
    },

    items: [
      {
        name: "Designer Sling Bag",
        quantity: 1,
        price: 999,
      },
    ],
  },
];

const HerKartOrders = () => {
  const [liveOrders, setLiveOrders] = useState([]);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("All");
  const [showSamples, setShowSamples] = useState(false);

  const { fetchAllOrders } = useHerKart();
  const [loadingOrders, setLoadingOrders] = useState(true);

  const markedSampleOrders = useMemo(
    () => sampleOrders.map((o) => ({ ...o, isSample: true })),
    []
  );

  const orders = useMemo(() => {
    // Sample orders temporarily disabled — kept for future demo use.
    // return showSamples ? markedSampleOrders : liveOrders;
    return liveOrders;
  }, [liveOrders, markedSampleOrders, showSamples]);

  const loadLiveOrders = async () => {
    setLoadingOrders(true);
    try {
      const response = await fetchAllOrders();
      if (response?.success) {
        setLiveOrders((response.orders || []).map((o) => ({ ...o, isSample: false })));
      }
    } catch (e) {
      console.error("Orders fetch error:", e);
    } finally {
      setLoadingOrders(false);
    }
  };

  useEffect(() => {
    loadLiveOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredOrders = useMemo(() => {
    let list = [...orders];

    if (status !== "All") {
      list = list.filter((order) => (order.status || "Pending") === status);
    }

    if (search.trim()) {
      const keyword = search.toLowerCase();

      list = list.filter((order) =>
        [
          order.orderNo,
          order.customer?.name,
          order.customer?.mobile,
          order.customer?.city,
          order.customer?.state,
        ]
          .join(" ")
          .toLowerCase()
          .includes(keyword),
      );
    }

    return list.sort((a, b) => new Date(b.orderedAt) - new Date(a.orderedAt));
  }, [orders, search, status]);

  return (
    <div className="min-h-screen bg-[#050816] text-white">
      <div className="sticky top-0 z-40 border-b border-white/10 bg-[#050816]/95 backdrop-blur">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4">
          <div className="flex items-center gap-3">
            <Link
              to="/herkart/dashboard"
              className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/10 hover:bg-white/20"
            >
              <FontAwesomeIcon icon={faArrowLeft} />
            </Link>

            <div className="flex flex-col justify-start">
              <h1 className="text-xl font-bold text-left">Orders</h1>

              <p className="text-xs text-slate-200">
                Merchant Order Management
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {/* Sample toggle temporarily disabled — kept for future demo use.
            <button
              onClick={() => setShowSamples((prev) => !prev)}
              className={`rounded-xl w-fit px-2 py-1 text-xs ${
                showSamples ? "bg-amber-500/20 text-amber-300" : "bg-white/10 hover:bg-white/20"
              }`}
            >
              {showSamples ? "Live Orders" : "Show Sample"}
            </button>
            */}
            <div className="rounded-xl bg-pink-600/20 px-4 py-2 text-pink-300">
              <span className="font-semibold">{filteredOrders.length} Orders</span>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 py-6">
        <div className="rounded-3xl border border-white/10 bg-white/5 p-5">
          <div className="grid gap-4 lg:grid-cols-2">
            <div className="relative">
              <FontAwesomeIcon
                icon={faSearch}
                className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500"
              />

              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search order, customer, city..."
                className="w-full rounded-xl border border-white/10 bg-[#0b1120] py-3 pl-11 pr-4 outline-none focus:border-pink-500"
              />
            </div>

            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2 text-sm text-slate-400">
                <FontAwesomeIcon icon={faFilter} />
                Status
              </div>

              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="flex-1 rounded-xl border border-white/10 bg-[#0b1120] px-2 py-1 outline-none focus:border-pink-500"
              >
                <option>All</option>
                <option>Pending</option>
                <option>Partially Delivered</option>
                <option>Delivered</option>
                <option>Cancelled</option>
              </select>
            </div>
          </div>
        </div>

        <div className="mt-6 space-y-4"></div>
        {filteredOrders.length === 0 ? (
          <div className="rounded-3xl border border-white/10 bg-white/5 px-6 py-16 text-center">
            <FontAwesomeIcon
              icon={faShoppingBag}
              className="mx-auto mb-5 text-6xl text-slate-500"
            />

            <h2 className="text-2xl text-cyan-200 font-bold">No Orders Found</h2>
            {/* <Link
              to="/herkart/bill"
              className="mt-6 inline-flex items-center rounded-xl bg-pink-600 px-6 py-3 font-semibold text-white transition hover:bg-pink-500"
            >
              Sample Bill
            </Link> */}

            <p className="mt-3 text-slate-400">
              No orders match the current search or filter.
            </p>
          </div>
        ) : (
          filteredOrders.map((order) => (
            <div
              key={order._id || order.orderNo}
              className={`rounded-3xl p-5 transition ${
                order.isSample
                  ? "border-2 border-dashed border-amber-500/40 bg-amber-500/[0.04] hover:border-amber-500/60"
                  : "border border-white/10 bg-white/5 hover:border-pink-500/40 hover:bg-white/[0.07]"
              }`}
            >
              <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
                <div className="flex-1">
                  <div className="flex flex-wrap items-center gap-3">
                    <h2 className="text-lg text-cyan-200 font-bold">{order.orderNo}</h2>

                    <span
                      className={`rounded-full px-3 py-1 text-xs font-semibold ${
                        (order.status || "Pending") === "Delivered"
                          ? "bg-emerald-500/20 text-emerald-300"
                          : (order.status || "Pending") === "Cancelled"
                            ? "bg-red-500/20 text-red-300"
                            : (order.status || "Pending") === "Partially Delivered"
                              ? "bg-orange-500/20 text-orange-300"
                              : "bg-yellow-500/20 text-yellow-300"
                      }`}
                    >

                    {order.isSample && (
                      <span className="ml-2 rounded-full bg-amber-500/20 px-2 py-0.5 text-[10px] font-semibold text-amber-300">
                        Sample
                      </span>
                    )}
                      {(order.status || "Pending") === "Delivered" && (
                        <FontAwesomeIcon
                          icon={faCheckCircle}
                          className="mr-1 inline"
                        />
                      )}

                      {(order.status || "Pending") === "Cancelled" && (
                        <FontAwesomeIcon
                          icon={faCircleXmark}
                          className="mr-1 inline"
                        />
                      )}

                      {(order.status || "Pending") === "Pending" && (
                        <FontAwesomeIcon
                          icon={faClock}
                          className="mr-1 inline"
                        />
                      )}

                      {order.status || "Pending"}
                    </span>
                  </div>

                  <div className="mt-4 grid gap-3 text-sm text-slate-400 md:grid-cols-2 lg:grid-cols-4">
                    <div>
                      <p className="text-xs uppercase tracking-wide text-slate-500">
                        Customer
                      </p>

                      <p className="mt-1 font-medium text-white">
                        {order.customer?.name}
                      </p>
                    </div>

                    <div>
                      <p className="text-xs uppercase tracking-wide text-slate-500">
                        Mobile
                      </p>

                      <p className="mt-1 font-medium text-white">
                        {order.customer?.mobile}
                      </p>
                    </div>

                    <div>
                      <p className="text-xs uppercase tracking-wide text-slate-500">
                        City
                      </p>

                      <p className="mt-1 font-medium text-white">
                        {order.customer?.city}
                      </p>
                    </div>

                    <div>
                      <p className="text-xs uppercase tracking-wide text-slate-500">
                        Ordered On
                      </p>

                      <p className="mt-1 font-medium text-white">
                        {new Date(order.orderedAt).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <div className="mt-5 rounded-2xl border border-white/10 bg-[#111827]/70 p-4">
                    <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-pink-400">
                      Your Items In This Order
                    </p>

                    <div className="space-y-2">
                      {(order.sellerItems || order.items).slice(0, 3).map((item, index) => (
                        <div
                          key={index}
                          className="flex items-center justify-between text-sm"
                        >
                          <span className="text-slate-200">{item.name}</span>

                          <span className="text-slate-400">
                            {item.quantity} × ₹{item.price}
                          </span>
                        </div>
                      ))}

                      {(order.sellerItems || order.items).length > 3 && (
                        <p className="text-xs text-pink-400">
                          + {(order.sellerItems || order.items).length - 3} more item(s)
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex flex-col items-start gap-4 lg:items-end">
                  <div className="text-right">
                    <p className="text-xs uppercase tracking-wide text-slate-500">
                      Your Items' Value
                    </p>

                    <p className="mt-1 text-2xl font-bold text-pink-400">
                      ₹{order.sellerSubtotal ?? order.grandTotal}
                    </p>

                    <p className="mt-1 text-sm text-slate-400">
                      {(order.sellerItems || order.items).length} item
                      {(order.sellerItems || order.items).length > 1 ? "s" : ""}
                    </p>
                  </div>

                  {order.isSample ? (
                    <span className="flex items-center gap-2 rounded-xl bg-white/5 px-5 py-3 font-medium text-slate-500 cursor-not-allowed">
                      <FontAwesomeIcon icon={faEye} />
                      Sample Only
                    </span>
                  ) : (
                    <Link
                      to={`/herkart/orders/${order.orderNo}`}
                      className="flex items-center gap-2 rounded-xl bg-pink-600 px-5 py-3 font-medium transition hover:bg-pink-500"
                    >
                      <FontAwesomeIcon icon={faEye} />
                      View Order
                    </Link>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default HerKartOrders;
